import { useState } from 'react';
import { XCircle, MessageCircle, Check, X, Info, Calendar, Clock, Send } from 'lucide-react';

// Dados simulados
const consulta = {
  paciente_nome: 'Beatriz Mendes Oliveira',
  categoria: 'Consulta Clínica',
  data_consulta: '2026-04-30',
  hora_inicio: '14:00',
};

const template_default = `Olá {nome_paciente}, informamos que sua consulta de {procedimento} do dia {data} às {horario} precisou ser cancelada.

Gostaríamos de sugerir os seguintes horários disponíveis para remarcação:
{horarios_sugeridos}

Para remarcar, entre em contato conosco.`;

const slots = [
  { display: '05/05/2026 (terça) às 09:00' },
  { display: '06/05/2026 (quarta) às 14:30' },
  { display: '08/05/2026 (sexta) às 10:00' },
];

const renderMessage = (template) => {
  const horariosText = slots.map((s, i) => `${i + 1}. ${s.display}`).join('\n');
  return template
    .replace(/{nome_paciente}/g, consulta.paciente_nome)
    .replace(/{procedimento}/g, consulta.categoria)
    .replace(/{data}/g, '30/04/2026')
    .replace(/{horario}/g, consulta.hora_inicio)
    .replace(/{horarios_sugeridos}/g, horariosText);
};

// ===================== MOCKUP 1 — Toggle Switch =====================
const Modal1 = () => {
  const [motivo, setMotivo] = useState('cancelado_medico');
  const [enviarMsg, setEnviarMsg] = useState(true);

  const msg = renderMessage(template_default);
  const isMedico = motivo === 'cancelado_medico';

  return (
    <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
      <div className="text-center mb-6">
        <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <XCircle className="w-7 h-7 text-red-600" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900">Cancelar Consulta</h3>
      </div>

      {/* Quem cancela */}
      <div className="mb-5">
        <label className="text-sm font-medium text-gray-700 mb-2 block">Quem está cancelando?</label>
        <div className="space-y-2">
          <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
            <input type="radio" checked={motivo === 'cancelado_paciente'} onChange={() => setMotivo('cancelado_paciente')} className="text-red-600" />
            <span className="text-sm">Paciente</span>
          </label>
          <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
            <input type="radio" checked={motivo === 'cancelado_medico'} onChange={() => setMotivo('cancelado_medico')} className="text-red-600" />
            <span className="text-sm">Médico / Clínica</span>
          </label>
        </div>
      </div>

      {/* Toggle WhatsApp (só quando médico) */}
      {isMedico && (
        <div className="mb-5 border-t border-gray-100 pt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-gray-900">Notificar paciente via WhatsApp</span>
            </div>
            <button
              onClick={() => setEnviarMsg(!enviarMsg)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enviarMsg ? 'bg-green-500' : 'bg-gray-300'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enviarMsg ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
          <p className="text-xs text-gray-500 ml-6">
            {enviarMsg ? 'Prévia abaixo. Sugestões de remarcação calculadas automaticamente.' : 'O paciente NÃO será notificado.'}
          </p>

          {/* Prévia */}
          {enviarMsg && (
            <div className="mt-3 bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-3 py-2 bg-gray-100 border-b border-gray-200 flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-wider font-bold text-gray-500">Prévia da mensagem</span>
                <Info className="w-3 h-3 text-gray-400" />
              </div>
              <pre className="p-3 text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed max-h-48 overflow-y-auto">{msg}</pre>
              <div className="px-3 py-2 bg-amber-50 border-t border-amber-200">
                <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider mb-1">Datas sugeridas</p>
                {slots.map((s, i) => (
                  <div key={i} className="text-xs text-amber-800 flex items-center gap-1.5">
                    <Calendar className="w-3 h-3" /> {s.display}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-3 mt-5">
        <button className="flex-1 py-2.5 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50">Voltar</button>
        <button className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium">Confirmar Cancelamento</button>
      </div>
    </div>
  );
};

// ===================== MOCKUP 2 — Side-by-side cards =====================
const Modal2 = () => {
  const [motivo, setMotivo] = useState('cancelado_medico');
  const [modo, setModo] = useState('com_msg'); // com_msg | sem_msg

  const msg = renderMessage(template_default);
  const isMedico = motivo === 'cancelado_medico';

  return (
    <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6">
      <div className="text-center mb-5">
        <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <XCircle className="w-7 h-7 text-red-600" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900">Cancelar Consulta</h3>
      </div>

      <div className="mb-5">
        <label className="text-sm font-medium text-gray-700 mb-2 block">Quem está cancelando?</label>
        <div className="grid grid-cols-2 gap-2">
          <label className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer ${motivo === 'cancelado_paciente' ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}>
            <input type="radio" checked={motivo === 'cancelado_paciente'} onChange={() => setMotivo('cancelado_paciente')} className="text-red-600" />
            <span className="text-sm font-medium">Paciente</span>
          </label>
          <label className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer ${motivo === 'cancelado_medico' ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}>
            <input type="radio" checked={motivo === 'cancelado_medico'} onChange={() => setMotivo('cancelado_medico')} className="text-red-600" />
            <span className="text-sm font-medium">Médico / Clínica</span>
          </label>
        </div>
      </div>

      {isMedico && (
        <div className="mb-5">
          <label className="text-sm font-medium text-gray-700 mb-2 block">Deseja notificar o paciente?</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setModo('com_msg')}
              className={`p-4 border-2 rounded-lg text-left transition-all ${modo === 'com_msg' ? 'border-green-500 bg-green-50 ring-2 ring-green-200' : 'border-gray-200 hover:border-gray-300'}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <MessageCircle className={`w-4 h-4 ${modo === 'com_msg' ? 'text-green-600' : 'text-gray-400'}`} />
                <span className={`text-sm font-bold ${modo === 'com_msg' ? 'text-green-900' : 'text-gray-700'}`}>Com mensagem</span>
                {modo === 'com_msg' && <Check className="w-4 h-4 text-green-600 ml-auto" />}
              </div>
              <p className="text-[11px] text-gray-500">Envia WhatsApp ao paciente com sugestões</p>
            </button>

            <button
              onClick={() => setModo('sem_msg')}
              className={`p-4 border-2 rounded-lg text-left transition-all ${modo === 'sem_msg' ? 'border-gray-500 bg-gray-100 ring-2 ring-gray-200' : 'border-gray-200 hover:border-gray-300'}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <X className={`w-4 h-4 ${modo === 'sem_msg' ? 'text-gray-700' : 'text-gray-400'}`} />
                <span className={`text-sm font-bold ${modo === 'sem_msg' ? 'text-gray-900' : 'text-gray-700'}`}>Sem notificação</span>
                {modo === 'sem_msg' && <Check className="w-4 h-4 text-gray-700 ml-auto" />}
              </div>
              <p className="text-[11px] text-gray-500">Cancela sem avisar o paciente</p>
            </button>
          </div>

          {/* Prévia fixa */}
          {modo === 'com_msg' && (
            <div className="mt-4 rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-3 py-2 bg-gray-900 flex items-center gap-2">
                <MessageCircle className="w-3.5 h-3.5 text-green-400" />
                <span className="text-[11px] uppercase tracking-wider font-bold text-gray-300">Prévia — WhatsApp</span>
              </div>
              <div className="p-3 bg-white">
                <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">{msg}</pre>
              </div>
              <div className="px-3 py-2 bg-blue-50 border-t border-blue-100">
                <p className="text-[10px] font-bold text-blue-700 uppercase tracking-wider mb-1 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Datas que serão sugeridas ao paciente
                </p>
                <div className="grid grid-cols-1 gap-0.5">
                  {slots.map((s, i) => (
                    <div key={i} className="text-xs text-blue-800 font-medium">→ {s.display}</div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-3 mt-5">
        <button className="flex-1 py-2.5 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50">Voltar</button>
        <button className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium">Confirmar Cancelamento</button>
      </div>
    </div>
  );
};

// ===================== MOCKUP 3 — WhatsApp-like preview =====================
const Modal3 = () => {
  const [motivo, setMotivo] = useState('cancelado_medico');
  const [enviar, setEnviar] = useState(true);

  const msg = renderMessage(template_default);
  const isMedico = motivo === 'cancelado_medico';

  return (
    <div className="bg-white rounded-xl shadow-xl max-w-xl w-full p-6">
      <div className="text-center mb-5">
        <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <XCircle className="w-7 h-7 text-red-600" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900">Cancelar Consulta</h3>
      </div>

      <div className="mb-4">
        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Quem está cancelando?</label>
        <div className="flex gap-2">
          <button onClick={() => setMotivo('cancelado_paciente')} className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border ${motivo === 'cancelado_paciente' ? 'bg-red-50 border-red-400 text-red-700' : 'bg-white border-gray-200 text-gray-600'}`}>Paciente</button>
          <button onClick={() => setMotivo('cancelado_medico')} className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border ${motivo === 'cancelado_medico' ? 'bg-red-50 border-red-400 text-red-700' : 'bg-white border-gray-200 text-gray-600'}`}>Médico / Clínica</button>
        </div>
      </div>

      {isMedico && (
        <div className="mb-4">
          {/* Checkbox */}
          <label className="flex items-start gap-3 p-3 bg-green-50/40 border border-green-200 rounded-lg cursor-pointer">
            <input type="checkbox" checked={enviar} onChange={(e) => setEnviar(e.target.checked)} className="mt-0.5 w-4 h-4 accent-green-600" />
            <div className="flex-1">
              <span className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                <MessageCircle className="w-3.5 h-3.5 text-green-600" />
                Enviar mensagem de cancelamento ao paciente
              </span>
              <span className="text-[11px] text-gray-500">Envia WhatsApp com sugestões de remarcação</span>
            </div>
          </label>

          {/* Bolha WhatsApp preview */}
          <div className="mt-4 bg-[#E5DDD5] rounded-lg p-3" style={{ backgroundImage: 'radial-gradient(circle, rgba(0,0,0,0.04) 1px, transparent 1px)', backgroundSize: '20px 20px' }}>
            <div className={`transition-all ${enviar ? 'opacity-100' : 'opacity-30 grayscale'}`}>
              <div className="text-[9px] uppercase tracking-wider font-bold text-gray-600 mb-1.5 flex items-center gap-1">
                <Send className="w-2.5 h-2.5" /> Prévia — a caminho de (11) 98139-1174
              </div>
              {/* WhatsApp bubble */}
              <div className="max-w-[90%] ml-auto bg-[#DCF8C6] rounded-lg rounded-tr-none p-2.5 shadow-sm">
                <pre className="text-[12px] text-gray-900 whitespace-pre-wrap font-sans leading-relaxed">{msg}</pre>
                <div className="text-[9px] text-gray-500 text-right mt-1">14:32 ✓✓</div>
              </div>

              {/* Datas destacadas */}
              <div className="mt-3 bg-white/70 backdrop-blur-sm rounded-lg p-2 border border-white">
                <p className="text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Datas sugeridas automaticamente
                </p>
                {slots.map((s, i) => (
                  <div key={i} className="text-xs text-gray-700 py-0.5 flex items-center gap-1.5">
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-green-500 text-white text-[9px] font-bold">{i + 1}</span>
                    {s.display}
                  </div>
                ))}
              </div>
            </div>
            {!enviar && (
              <div className="mt-2 text-center bg-gray-700/90 text-white text-xs font-medium py-1.5 px-3 rounded flex items-center justify-center gap-2">
                <X className="w-3.5 h-3.5" /> Mensagem NÃO será enviada
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex gap-3 mt-5">
        <button className="flex-1 py-2.5 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50">Voltar</button>
        <button className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium">Confirmar Cancelamento</button>
      </div>
    </div>
  );
};

export default function CancelamentoMockupsPage() {
  const SectionTitle = ({ n, name, desc }) => (
    <div className="mb-4">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-bold">{n}</div>
        <h2 className="text-xl font-bold text-slate-900">{name}</h2>
      </div>
      <p className="text-sm text-slate-500 mt-1.5 ml-11">{desc}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100 p-8">
      <div className="max-w-5xl mx-auto">
        <header className="mb-10">
          <h1 className="text-3xl font-bold text-slate-900">Modal de Cancelamento — 3 Propostas</h1>
          <p className="text-slate-500 mt-2">Escolher se envia WhatsApp ao paciente + prévia com template e datas sugeridas.</p>
        </header>

        <section className="mb-10 bg-white rounded-2xl p-6 shadow-sm">
          <SectionTitle
            n="1"
            name="Toggle Switch + Prévia expansível"
            desc="Um toggle iOS-like controla o envio. Prévia expande/recolhe conforme o toggle. Visual minimalista, ideal para quem já conhece o fluxo."
          />
          <div className="flex justify-center py-4">
            <Modal1 />
          </div>
        </section>

        <section className="mb-10 bg-white rounded-2xl p-6 shadow-sm">
          <SectionTitle
            n="2"
            name="Cards de escolha + Prévia estruturada"
            desc="Duas opções grandes lado a lado (Com/Sem mensagem). Prévia com cabeçalho dark estilo terminal e bloco azul separado para as datas sugeridas."
          />
          <div className="flex justify-center py-4">
            <Modal2 />
          </div>
        </section>

        <section className="mb-10 bg-white rounded-2xl p-6 shadow-sm">
          <SectionTitle
            n="3"
            name="Prévia tipo WhatsApp real"
            desc="Checkbox simples + prévia dentro de uma bolha verde do WhatsApp com fundo texturizado. Quando desmarcado, a bolha fica acinzentada e mostra 'Mensagem NÃO será enviada'. Datas sugeridas em destaque."
          />
          <div className="flex justify-center py-4">
            <Modal3 />
          </div>
        </section>

        <footer className="text-center text-sm text-slate-400 mt-12 pb-6">
          Template e datas sugeridas vêm das configurações; datas são calculadas pelo backend com base em horários livres.
        </footer>
      </div>
    </div>
  );
}
